import { z } from 'zod';
import { CurrencyCode, HouseEdgeBps, MinorUnits } from './money';
import { LobbyPayoutRule } from './lobby';
import { GameRoundResult } from './result';
import { VersusPayoutRule, VersusRoundWinner, VersusSeat } from './versus';

/**
 * The contract between the platform and a game's maths, version 2.0.
 *
 * An engine is a pure function of the bet and the seed. It holds no session,
 * no balance and no randomness of its own: the platform generates the seed,
 * stores it on the round, and can therefore replay any round against any
 * engine instance and get the same answer. That is what lets a game ship as
 * its own stateless container without the platform losing auditability.
 *
 * Nothing here names a game. The platform understands money, seeds, policy
 * and whether a round is still open; a bet, a result and a round state are
 * opaque to it and checked against the schema the engine publishes. That is
 * what lets a game the platform has never heard of be routed without a
 * platform release.
 *
 * Normative description: `gsp-src/docs/engine-contract/round-single.md`.
 */

/**
 * How a game is played, and the only thing that decides which calls the
 * platform makes against it.
 */
export const EngineGameMode = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('INSTANT') }),
  z.object({
    kind: z.literal('MULTI_STEP'),
    /** How long a round may stay open before the platform settles it. */
    autoSettleAfterMs: z.number().int().positive(),
    /**
     * How many rounds one player may have open. An operator may only tighten
     * this: the effective limit is `min(game, operator)`.
     */
    maxConcurrentRoundsPerPlayer: z.number().int().positive(),
  }),
  z.object({ kind: z.literal('LOBBY') }),
  z.object({ kind: z.literal('VERSUS') }),
]);
export type EngineGameMode = z.infer<typeof EngineGameMode>;

/** The seed material the platform generated for this round. */
export const EngineProofInput = z.object({
  serverSeed: z.string().min(1),
  clientSeed: z.string().min(1),
  nonce: z.string().regex(/^\d+$/),
  /** The commitment published before the bet, for the engine to echo. */
  serverSeedHash: z.string().optional(),
});
export type EngineProofInput = z.infer<typeof EngineProofInput>;

/**
 * What the operator's configuration says, kept apart from the bet because it
 * is the platform's to set and the player's to be protected by. A game that
 * found these inside `bet` could mistake them for something a player chose.
 */
export const EnginePolicy = z
  .object({
    houseEdgeBps: HouseEdgeBps,
    maxPayout: MinorUnits,
    currency: CurrencyCode,
  })
  // Whatever else the operator has configured for this game travels through
  // here untouched. The platform cannot enforce a dice target window or a
  // mines bomb ceiling without knowing what those are; the engine can, and
  // this is how it is told.
  .catchall(z.unknown());
export type EnginePolicy = z.infer<typeof EnginePolicy>;

/**
 * The bet. Only `amount` is the platform's business — it is what the wallet
 * was debited — and the rest is checked against the game's own schema.
 */
export const EngineBet = z.object({ amount: MinorUnits }).catchall(z.unknown());
export type EngineBet = z.infer<typeof EngineBet>;

/** What the engine consumed while deriving the outcome. */
export const EngineProof = z.object({
  hmacInput: z.string(),
  cursors: z.array(
    z.object({
      cursor: z.number().int().nonnegative(),
      bytes: z.string(),
      use: z.string().optional(),
    }),
  ),
});
export type EngineProof = z.infer<typeof EngineProof>;

/** One call, one settled round. */
export const EngineEvaluateRequest = z.object({
  gameCode: z.string().min(1).max(64),
  proof: EngineProofInput,
  bet: EngineBet,
  policy: EnginePolicy,
});
export type EngineEvaluateRequest = z.infer<typeof EngineEvaluateRequest>;

export const EngineEvaluateResponse = z.object({
  status: z.literal('OK'),
  result: GameRoundResult,
  proof: EngineProof,
});
export type EngineEvaluateResponse = z.infer<typeof EngineEvaluateResponse>;

/** `BEGIN` opens a round, `ACTION` advances it, `AUTO_SETTLE` closes it. */
export const EngineTransitionOp = z.enum(['BEGIN', 'ACTION', 'AUTO_SETTLE']);
export type EngineTransitionOp = z.infer<typeof EngineTransitionOp>;

/**
 * The engine's serialisation of an open round, opaque to the platform and
 * handed back untouched on the next call. `phase` is the one reserved field:
 * the platform reads `SETTLED` to know the round is over.
 */
export const EngineRoundState = z.object({ phase: z.string().min(1) }).catchall(z.unknown());
export type EngineRoundState = z.infer<typeof EngineRoundState>;

/** Opaque to the platform; `type` names the move for the game's own sake. */
export const EngineAction = z.object({ type: z.string().min(1) }).catchall(z.unknown());
export type EngineAction = z.infer<typeof EngineAction>;

export const EngineTransitionRequest = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('BEGIN'),
    gameCode: z.string().min(1).max(64),
    proof: EngineProofInput,
    bet: EngineBet,
    policy: EnginePolicy,
  }),
  z.object({
    op: z.literal('ACTION'),
    gameCode: z.string().min(1).max(64),
    action: EngineAction,
    state: EngineRoundState,
    policy: EnginePolicy,
  }),
  z.object({
    op: z.literal('AUTO_SETTLE'),
    gameCode: z.string().min(1).max(64),
    state: EngineRoundState,
    policy: EnginePolicy,
  }),
]);
export type EngineTransitionRequest = z.infer<typeof EngineTransitionRequest>;

export const EngineTransitionResponse = z.object({
  status: z.literal('OK'),
  state: EngineRoundState,
  /**
   * The same round as the player may see it.
   *
   * `state` has to hold everything the engine needs to resume — the mine
   * positions, the dealer's hole card, the seed — and none of that can go to
   * the browser. So the engine says separately what may: this is what the
   * round endpoints answer with, and `state` never leaves the platform.
   *
   * `phase` is required here too. `allowedActions: string[]` is the
   * convention a generic client reads to know which moves to offer; a game
   * with a client of its own can put whatever that client needs here.
   *
   * Optional only for engines written against 2.0. One that publishes no
   * view has its phase served alone, which is a poor round to play but not
   * a broken one.
   */
  view: EngineRoundState.optional(),
  /** `null` while the round is open, the settled result once `phase` is `SETTLED`. */
  result: GameRoundResult.nullable(),
  /**
   * Additional stake this step requires — a double-down, a split, insurance.
   * Only `ACTION` may price; `BEGIN` and `AUTO_SETTLE` must return `'0'`.
   *
   * The platform debits it and commits the returned state only once the debit
   * is confirmed, so a refused raise leaves the round exactly where it was.
   */
  extraBet: MinorUnits,
  proof: EngineProof.optional(),
});
export type EngineTransitionResponse = z.infer<typeof EngineTransitionResponse>;

export const EngineLobbyResolveRequest = z.object({
  gameCode: z.string().min(1).max(64),
  /** Platform round identifier, for engine-side logging only. */
  roundId: z.string().min(1).max(64).optional(),
  serverSeed: z.string().min(1),
  drandRandomness: z.string().min(1),
  drandRound: z.number().int().nonnegative(),
  houseEdgeBps: HouseEdgeBps,
});
export type EngineLobbyResolveRequest = z.infer<typeof EngineLobbyResolveRequest>;

export const EngineLobbyResolveResponse = z.object({
  status: z.literal('OK'),
  /** Opaque, game-specific. Crash puts `crashPoint` here. */
  outcome: z.record(z.string(), z.unknown()),
  payoutRule: LobbyPayoutRule,
  /** How long the run phase lasts before the round resolves. */
  runDurationMs: z.number().int().nonnegative(),
  /**
   * The multiplier the round ends at. The host schedules the end of the run
   * phase against it and refuses any cash-out beyond it, which is how it
   * settles a round without reading the opaque outcome. A game whose payout is
   * not multiplier-shaped returns 1.
   */
  finalMultiplier: z.number().min(1),
});
export type EngineLobbyResolveResponse = z.infer<typeof EngineLobbyResolveResponse>;

/**
 * Versus mode: decide a round, and say whether the match is over.
 *
 * The engine holds no match state, so the platform sends everything the
 * decision depends on: this round's revealed moves, the match configuration
 * the players agreed to, and the rounds already decided. It sends seats rather
 * than player identities, and never a stake or a balance.
 */
export const EngineVersusResolveRequest = z.object({
  gameCode: z.string().min(1).max(64),
  /** Platform match identifier, for engine-side logging only. */
  matchId: z.string().min(1).max(64).optional(),
  /** Opaque game configuration the match was created with, e.g. `{ bestOf: 3 }`. */
  config: z.record(z.string(), z.unknown()),
  /** The moves revealed for the round being decided, keyed by seat. */
  moves: z.object({
    CREATOR: z.record(z.string(), z.unknown()),
    CHALLENGER: z.record(z.string(), z.unknown()),
  }),
  /** Winners of the rounds already decided, oldest first. */
  history: z.array(VersusRoundWinner),
  houseEdgeBps: HouseEdgeBps,
});
export type EngineVersusResolveRequest = z.infer<typeof EngineVersusResolveRequest>;

export const EngineVersusResolveResponse = z.object({
  status: z.literal('OK'),
  roundWinner: VersusRoundWinner,
  /** The match winner, or null when the series needs more rounds. */
  matchWinner: VersusSeat.nullable(),
  /** Applies once `matchWinner` is set. */
  payoutRule: VersusPayoutRule,
  /** Opaque, game-specific. Kept with the round for history and proofs. */
  outcome: z.record(z.string(), z.unknown()),
});
export type EngineVersusResolveResponse = z.infer<typeof EngineVersusResolveResponse>;

/**
 * What the platform reads at start-up to check a container is serving the game
 * and the math version it expects.
 */
export const EngineDescribeResponse = z.object({
  status: z.literal('OK'),
  games: z.array(
    z.object({
      gameCode: z.string(),
      name: z.string(),
      mathVersion: z.string(),
      rtp: z.number(),
    }),
  ),
});
export type EngineDescribeResponse = z.infer<typeof EngineDescribeResponse>;

/**
 * A setting an admin may change, and the bounds the engine still holds for.
 *
 * The engine declares it because it is the only party that knows what its
 * maths was designed and certified for; the admin API refuses a configuration
 * outside these bounds, so a bad number is caught on entry rather than in a
 * payout.
 *
 * `key` is the whole interface. What an operator sets under it is stored on
 * `operator_games.policy`, sent to the engine as `policy[key]` and published
 * to the client as `config[key]`, all without the platform knowing what it
 * means. A game that wants an operator knob declares a tunable; it never
 * needs a column of its own.
 */
const DECIMAL_STRING = z
  .string()
  .regex(/^-?\d+(?:\.\d+)?$/, 'decimal tunable bounds must be plain decimal strings');

export const EngineTunable = z.discriminatedUnion('type', [
  z.object({
    key: z.string(),
    type: z.enum(['INTEGER', 'NUMBER']),
    min: z.number(),
    max: z.number(),
    default: z.number(),
  }),
  z.object({ key: z.string(), type: z.literal('BOOLEAN'), default: z.boolean() }),
  z.object({
    key: z.string(),
    type: z.literal('ENUM'),
    values: z.array(z.union([z.string(), z.number()])).min(1),
    default: z.union([z.string(), z.number()]),
  }),
  /**
   * A fixed-point number carried as a decimal string, for the same reason
   * money is: a dice target of `1.10` is not `1.1000000000000001`, and a
   * value that round-trips through a double cannot be compared to a bound
   * the engine wrote down. `scale` is the number of decimal places allowed.
   */
  z.object({
    key: z.string(),
    type: z.literal('DECIMAL'),
    min: DECIMAL_STRING,
    max: DECIMAL_STRING,
    scale: z.number().int().min(0).max(8),
    default: DECIMAL_STRING,
  }),
]);
export type EngineTunable = z.infer<typeof EngineTunable>;

/** Admin-facing describe (versioned aggregator contract). */
export const EngineAdminDescribe = z.object({
  status: z.literal('OK'),
  games: z.array(
    z.object({
      gameCode: z.string(),
      name: z.string(),
      mathVersion: z.string(),
      mode: EngineGameMode,
      rtp: z.number().optional(),
      tunables: z.array(EngineTunable).optional(),
      /**
       * Whatever a client needs in order to draw the game — a paytable, a
       * grid size, a multiplier ladder. Opaque to the platform, which stores
       * it and hands it to the client untouched.
       */
      config: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
  mathVersion: z.string(),
  sdkVersion: z.string(),
  contractVersion: z.string(),
  mode: z.enum(['ROUND_SINGLE', 'LOBBY', 'VERSUS']),
  /**
   * Identifies the running image. The gate stores the hash that passed, so a
   * change without a new gate means the approved maths is not what is running.
   */
  buildHash: z.string().optional(),
  /** Request schema per game code, so one handshake describes the engine. */
  schemas: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});
export type EngineAdminDescribe = z.infer<typeof EngineAdminDescribe>;

export const EngineAdminSchema = z.object({
  status: z.literal('OK'),
  gameCode: z.string(),
  schema: z.record(z.string(), z.unknown()),
});
export type EngineAdminSchema = z.infer<typeof EngineAdminSchema>;

export const EngineAdminSimulateRequest = z.object({
  rounds: z.number().int().positive(),
  bet: z.record(z.string(), z.unknown()).optional(),
});
export type EngineAdminSimulateRequest = z.infer<typeof EngineAdminSimulateRequest>;

export const EngineAdminSimulateResponse = z.object({
  status: z.literal('OK'),
  rounds: z.number().int().nonnegative(),
  rtp: z.number(),
  totalWagered: z.number(),
  totalWon: z.number(),
});
export type EngineAdminSimulateResponse = z.infer<typeof EngineAdminSimulateResponse>;

/**
 * Many rounds in one call, so the platform measures RTP rather than asking
 * the engine what its RTP is. An engine cannot misreport a number it never
 * calculates.
 */
export const EngineAdminEvaluateBatchRequest = z.object({
  gameCode: z.string(),
  rounds: z
    .array(
      z.object({
        proof: z.object({
          serverSeed: z.string(),
          clientSeed: z.string(),
          nonce: z.string(),
        }),
        bet: z.record(z.string(), z.unknown()),
      }),
    )
    .min(1)
    .max(10_000),
});
export type EngineAdminEvaluateBatchRequest = z.infer<typeof EngineAdminEvaluateBatchRequest>;

export const EngineAdminEvaluateBatchResponse = z.object({
  status: z.literal('OK'),
  results: z.array(
    z.object({
      /** Minor units, as strings: a batch of a million rounds overflows a double. */
      totalBet: z.string().regex(/^\d+$/),
      totalWin: z.string().regex(/^\d+$/),
      result: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});
export type EngineAdminEvaluateBatchResponse = z.infer<typeof EngineAdminEvaluateBatchResponse>;

export const EngineAdminTestVectors = z.object({
  status: z.literal('OK'),
  vectors: z.array(
    z.object({
      id: z.string(),
      input: z.record(z.string(), z.unknown()),
      expected: z.record(z.string(), z.unknown()),
    }),
  ),
});
export type EngineAdminTestVectors = z.infer<typeof EngineAdminTestVectors>;
